import { createInputFrame } from '../../core';
import type { InputFrame, LogicalAction } from '../../core';
import type { AppSettings, PlayerKeyBindings, SettingsAction } from '../../infrastructure/settings/settings-schema';

const P1_KEYS: Readonly<Record<LogicalAction, readonly string[]>> = Object.freeze({
  left: ['KeyA'], right: ['KeyD'], down: ['KeyS'], jump: ['KeyW'], light: ['KeyF'], heavy: ['KeyG'],
  skill1: ['KeyH'], skill2: ['KeyJ'], guard: ['KeyK'], super: ['Space'],
});
const P2_KEYS: Readonly<Record<LogicalAction, readonly string[]>> = Object.freeze({
  left: ['ArrowLeft'], right: ['ArrowRight'], down: ['ArrowDown'], jump: ['ArrowUp'],
  light: ['Numpad1', 'KeyU'], heavy: ['Numpad2', 'KeyI'], skill1: ['Numpad4', 'KeyO'],
  skill2: ['Numpad5', 'KeyP'], guard: ['Numpad6', 'BracketLeft'], super: ['Numpad0', 'KeyL'],
});

export class KeyboardInputAdapter {
  private readonly pressed = new Set<string>();
  private readonly onDown = (event: Event): void => { if (event instanceof KeyboardEvent) this.pressed.add(event.code); };
  private readonly onUp = (event: Event): void => { if (event instanceof KeyboardEvent) this.pressed.delete(event.code); };
  public constructor(private readonly target: Pick<Window, 'addEventListener' | 'removeEventListener'> = globalThis.window,
    private readonly settings?: Readonly<{ get(): AppSettings }>) {
    target.addEventListener('keydown', this.onDown); target.addEventListener('keyup', this.onUp);
  }
  public sample(player: 0 | 1): InputFrame {
    const keys = this.settings === undefined ? (player === 0 ? P1_KEYS : P2_KEYS)
      : toCoreBindings(player === 0 ? this.settings.get().keyBindings.p1 : this.settings.get().keyBindings.p2);
    return createInputFrame(Object.fromEntries(Object.entries(keys).map(([action, codes]) => [
      action, codes.some((code) => this.pressed.has(code)),
    ])));
  }
  public dispose(): void {
    this.target.removeEventListener('keydown', this.onDown); this.target.removeEventListener('keyup', this.onUp);
    this.pressed.clear();
  }
}
export const keyboardBindings = Object.freeze({ p1: P1_KEYS, p2: P2_KEYS });

const actionMap: Readonly<Record<LogicalAction, SettingsAction>> = Object.freeze({
  left: 'MOVE_LEFT', right: 'MOVE_RIGHT', jump: 'JUMP', down: 'CROUCH', light: 'LIGHT_ATTACK',
  heavy: 'HEAVY_ATTACK', skill1: 'SKILL_1', skill2: 'SKILL_2', super: 'SUPER', guard: 'GUARD',
});
export const toCoreBindings = (bindings: PlayerKeyBindings): Readonly<Record<LogicalAction, readonly string[]>> =>
  Object.freeze(Object.fromEntries(Object.entries(actionMap).map(([action, settingAction]) => [action, bindings[settingAction]])) as unknown as Record<LogicalAction, readonly string[]>);
